import React from 'react';

import { Telemetry } from '~commonInfra/services/Telemetry';
import { SiteSettingSiteAdminTelemetryEvent } from '~commonInfra/telemetryEvents/TelemetryEvents';
import {
  buildSiteSettingSiteAdminTelemetryEvent,
  getSiteSettingUUID,
} from '~commonInfra/telemetryEvents/util/SiteSettingTelemetryUtil';
import { NavLocationSelectors } from '~src/spacesPlatform/selectors/NavigationStateSelectors';

import { IResult } from '../../../../httpClient/serverApi/types/IResponse';
import { Sites } from '../../../../siteManagement/services/resources/Sites';
import { SettingsFormikContainer } from '../../../components/common/SettingsFormikContainer';
import { GeneralSiteSettingsSubspaceState } from '../../../components/subspaces/SiteSettingsSpace/GeneralSiteSettingsSubspace';
import {
  ISiteAdminSettings,
  ISiteAdminSettingsWithSubscriptions,
  ISiteSettingsForSiteAdmin,
} from '../../../types/ISiteSettingsForSiteAdmin';
import { useSelector } from '../../utils';

/**
 * The settings passed here are really the top-level settings, but because we're
 * recursively passing the prop down, we have to keep it named "settings"
 */
export type ReactSiteSettingsForSiteAdminSubFormikProps = {
  settings: Omit<GeneralSiteSettingsSubspaceState, 'siteAuthenticationSettings'>;
};

const sections = [
  'SiteInviteNotificationSection',
  'UpdateSiteLogoSection',
  'SiteStartPageSection',
  'AllowPulseFeatureSection',
  'TableauGAISection',
  'GenerativeAISection',
  'PersonalizedInsightRankingSection',
  'UserVisibilitySection',
  'DataOrientationAdminSettingsSection',
  'ExplainDataSection',
  'DataStorySection',
  'ExternalActionsWorkflowsSection',
  'DataChangeRadarSection',
  'AutomaticAccessSection',
  'SensitiveLineageDataSection',
  'ExtractEncryptionSection',
  'KMSInOnlineSection',
  'SupportAccessSection',
  'SharingSection',
  'CommentingSettingSection',
  'DataAlertsSection',
  'SubscriptionsSection',
  'AutoQualityMonitoringSection',
  'DataQualitySubscriptionSection',
  'TaggingSection',
  'VizRecommendationsSection',
  'RequestAccessSection',
  'CrossDataBaseJoinsSection',
  'MetricsContentTypeSection',
  'SiteTimeZoneSection',
  'NotifySiteAdminsOnThrottleSection',
  'FlowParametersSiteSettingSection',
  'RunNowSiteSettingSection',
  'LinkedTaskSiteSettingSection',
  'UserNotificationsSiteSettingsSection',
  'NotificationEmailSettingsSection',
  'EmailCustomizationSettingsSection',
  'FlowSubscriptionsSiteSettingSection',
  'NotificationFlowRunsSection',
  'WebPageObjectsSection',
  'PersonalSpaceSection',
  'CustomDomainSection',
  'CollectionsSettingSection',
  'OAuthClientsRegistrySection',
  'AllowPatCreationSection',
  'DataAccelerationSection',
  'AdminInsightsPublishFrequencySection',
  'ContentMigrationAuthorizationSection',
  'SiteGroupAssertionsSection',
  'SiteGroupSetsSection'
];

export function ReactSiteSettingsForSiteAdmin(props: ReactSiteSettingsForSiteAdminSubFormikProps) {
  const { settings, ...rest } = props;
  const siteId = getSiteIdFromGeneralSettings(settings);

  const submitHandler = useSubmitHandler(settings);

  // Adapter to move subscription settings down a level with the rest of settings
  // instead of on its own tier
  const siteAdminSettingsWithSubscriptions: ISiteAdminSettingsWithSubscriptions = React.useMemo(() => ({
    ...settings.siteSettings.siteAdminSettings as ISiteAdminSettings,
    subscriptionSettings: settings.siteSettings.subscriptionSettings
  }), [settings?.siteSettings?.siteAdminSettings, settings?.siteSettings?.subscriptionSettings]);

  return (
    <SettingsFormikContainer
      formName={'ReactSiteSettingsForSiteAdmin'}
      {...rest}
      settings={siteAdminSettingsWithSubscriptions}
      siteId={siteId}
      sections={sections}
      onSubmit={submitHandler}
     >
    </SettingsFormikContainer>
  );
}

function useSubmitHandler(settings: ReactSiteSettingsForSiteAdminSubFormikProps['settings']) {
  const isInSettingsLocation = useSelector(NavLocationSelectors.inSettingsLocation);
  return React.useCallback(async (newSiteAdminSettings: ISiteAdminSettingsWithSubscriptions) => {
    const oldSiteSettingsForSiteAdmin: ISiteSettingsForSiteAdmin = settings.siteSettings;

    const siteId = getSiteIdFromGeneralSettings(settings);

    const sitesService = Sites.instance;
    const result: ISiteSettingsForSiteAdmin | IResult = await sitesService.updateSiteSettingsForSiteAdmin(
      siteId, newSiteAdminSettings, newSiteAdminSettings.subscriptionSettings
    );

    if (result?.errors?.length) {
      throw result.errors.map((e: any) => sitesService.errorMessage(e.code, null));
    } else {
      // TODO: Gate this behind certain use-cases
      if (!isInSettingsLocation) {
        return;
      }

      const newSiteSettingsForSiteAdmin = result as ISiteSettingsForSiteAdmin;

      // send siteAdminSettings setting for siteAdminSettings admin telemetry events
      if (shouldSendTelemetryForSite(newSiteAdminSettings as ISiteAdminSettings)
        && shouldSendTelemetryForSite(oldSiteSettingsForSiteAdmin.siteAdminSettings as ISiteAdminSettings)) {
        Telemetry.sendEvent<SiteSettingSiteAdminTelemetryEvent>(
          buildSiteSettingSiteAdminTelemetryEvent(
            newSiteSettingsForSiteAdmin,
            oldSiteSettingsForSiteAdmin,
            getSiteSettingUUID(true)
          )
        );
      }

      return {
        settings: {
          ...(newSiteSettingsForSiteAdmin.siteAdminSettings),
          subscriptionSettings: newSiteSettingsForSiteAdmin.subscriptionSettings
        }
      };
    }

  },
  // This deps array was noncompliant before we enabled the lint rule. If you're here, please consider fixing it! See TFS 1257824
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [
    isInSettingsLocation,
    settings?.siteSettings,
    settings?.siteSettings?.siteId,
    settings?.siteSettingsForServerAdmin?.id
  ]);
}

function getSiteIdFromGeneralSettings(settings: ReactSiteSettingsForSiteAdminSubFormikProps['settings']) {
  return (settings.siteSettings as any).id
    ?? settings.siteSettings.siteId
    ?? settings?.siteSettingsForServerAdmin?.id;
}

function shouldSendTelemetryForSite(siteAdminSettings: ISiteAdminSettings) {
  return siteAdminSettings && siteAdminSettings.siteNamedSharingSettings
    && siteAdminSettings.sitePersonalSpaceSettings && siteAdminSettings.askDataMode && siteAdminSettings.userVisibility;
}
